import { JSX, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaSave } from 'react-icons/fa';
import { FaCheck, FaKey, FaUser } from 'react-icons/fa6';
import { MdLogout, MdOutlineEmail, MdShowChart, MdVisibility, MdVisibilityOff } from 'react-icons/md';
import { useShallow } from 'zustand/shallow';
import { Button } from '../../components/button';
import { Error } from '../../components/error';
import { Form } from '../../components/form';
import { useAuthContext } from '../../contexts/auth.context';
import { UserRole } from '../../dto/user';
import { useFirebase } from '../../hooks/firebase.hook';
import { useHabit } from '../../hooks/habit.hook';
import { useLayout } from '../../hooks/layout.hook';
import { useNavigation } from '../../hooks/navigation.hook';
import { usePartnership } from '../../hooks/partnership.hook';
import { useAppStore } from '../../stores/app.store';

interface NameFormData {
  name: string;
}

interface PasswordFormData {
  password: string;
}

export default function SettingScreen(): JSX.Element {
  const { t } = useTranslation();
  const { signOut } = useAuthContext();
  const { user, updateName, deleteAccount } = useAuthContext();
  const { translateError } = useFirebase();
  const { deleteHabits } = useHabit();
  const { deletePartnerships } = usePartnership();
  const { navigate } = useNavigation();

  const [isDark, isThemeForced, toggleTheme] = useAppStore(
    useShallow((s) => [s.isDark, s.isThemeForced, s.toggleTheme]),
  );

  const [isSaving, setIsSaving] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isDelete, setIsDelete] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string>();
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  async function handleNameChange({ name }: NameFormData) {
    setIsSaving(true);
    setError(undefined);

    updateName(name)
      .then(() => {
        setIsSuccess(true);
        setTimeout(() => setIsSuccess(false), 1000);
      })
      .catch((e) => setError(translateError(e.message)))
      .finally(() => setIsSaving(false));
  }

  async function handleDelete({ password }: PasswordFormData) {
    setIsDeleting(true);
    setError(undefined);

    deleteHabits()
      .then(() => deletePartnerships())
      .then(() => deleteAccount(password))
      .catch((e) => setError(translateError(e.message)))
      .finally(() => {
        setIsDeleting(false);
        setIsDelete(false);
      });
  }

  useLayout(() => ({ activeMenu: 'settings', title: isDelete ? t('Delete account') : t('Settings') }), [t, isDelete]);

  return isDelete ? (
    <Form onSubmit={handleDelete} className="flex flex-col gap-2">
      <div className="hyphens-auto text-base-content/70">
        {t('Please enter your password to confirm the deletion of your account and all associated data.')}
      </div>

      <div>
        <label className="input input-lg validator w-full relative">
          <FaKey className="text-base-content/70" />
          <input type={showPassword ? 'text' : 'password'} name="password" placeholder="*****" required />

          <div
            className="top-0 right-0 absolute h-full flex items-center cursor-pointer p-3"
            onClick={() => setShowPassword((prev) => !prev)}
          >
            {showPassword ? <MdVisibility size={'1.5rem'} /> : <MdVisibilityOff size={'1.5rem'} />}
          </div>
        </label>
        <div className="validator-hint hidden">{t('Required')}</div>
      </div>

      <div className="flex gap-4 mt-2">
        <button className="btn btn-neutral btn-lg flex-1" type="button" onClick={() => setIsDelete(false)}>
          {t('Cancel')}
        </button>

        <Button className="btn btn-error btn-lg flex-1" type="submit" loading={isDeleting}>
          {t('Delete')}
        </Button>
      </div>
    </Form>
  ) : (
    <>
      <div className="flex-grow-1 flex flex-col gap-5">
        {error && <Error msg={error} />}

        <Form onSubmit={handleNameChange} className="flex flex-col gap-2">
          <h3 className="text-center font-bold">{t('User Data')}</h3>

          <div className="join flex">
            <div className="flex-grow-1">
              <label className="input input-lg validator join-item w-full">
                <FaUser className="text-base-content/70" />
                <input type="text" name="name" placeholder="" defaultValue={user?.name} required />
              </label>
              <div className="validator-hint hidden">{t('Required')}</div>
            </div>
            <Button className="btn btn-primary btn-lg join-item" type="submit" loading={isSaving} disabled={isSuccess}>
              {isSuccess ? <FaCheck className="text-success" /> : <FaSave />}
            </Button>
          </div>

          <div>
            <label className="input input-lg validator w-full">
              <MdOutlineEmail className="text-base-content/70" />
              <div className="text-base-content/70">{user?.mail}</div>
            </label>
          </div>
        </Form>

        <button className="btn btn-lg btn-neutral" onClick={signOut}>
          <MdLogout /> {t('Logout')}
        </button>

        <button className="btn btn-lg btn-neutral" onClick={() => setIsDelete(true)}>
          {t('Delete account')}
        </button>

        {user?.role === UserRole.ADMIN && (
          <>
            <div className="divider my-0" />

            <button className="btn btn-lg btn-neutral" onClick={() => navigate('/app-stats', { replace: false })}>
              <MdShowChart /> {t('App Statistics')}
            </button>
          </>
        )}

        <div className="divider my-0" />

        {!isThemeForced && (
          <div className="flex items-center justify-center gap-2 text-base-content/70">
            <label htmlFor="theme-toggle" className="text-lg">
              {isDark ? t('Dark Mode') : t('Light Mode')}
            </label>
            <input
              id="theme-toggle"
              type="checkbox"
              className="toggle toggle-primary"
              checked={isDark}
              onChange={toggleTheme}
            />
          </div>
        )}

        <div className="text-center">
          <div className="text-base-content/70">
            {t('Do you need help or do you have any suggestions for improvement?')}
          </div>
          <a className="link link-accent" href="mailto:sup.virtus.app@gmail.com">
            {t('Contact Support')}
          </a>
        </div>

        <div className="flex-grow-1 flex flex-col justify-end text-center">
          <a className="link link-accent" onClick={() => navigate('/privacy', { replace: false })}>
            {t('Legal terms')}
          </a>

          <div
            className="text-center text-base-content/70 text-sm leading-none"
            onContextMenu={(e) => {
              e.preventDefault();
              navigate('/releases', { replace: false });
            }}
          >
            <div>Virtus App</div>
            <div>v{import.meta.env.APP_VERSION}</div>
          </div>
        </div>
      </div>
    </>
  );
}
